import * as THREE from 'https://esm.sh/three@0.163.0';
import { GLTFLoader } from 'https://esm.sh/three@0.163.0/examples/jsm/loaders/GLTFLoader.js';
import { OrbitControls } from 'https://esm.sh/three@0.163.0/examples/jsm/controls/OrbitControls.js';
import { RoomEnvironment } from 'https://esm.sh/three@0.163.0/examples/jsm/environments/RoomEnvironment.js';
// Camera positie (X,Y,Z)

//const DEFAULT_CAM = new THREE.Vector3(5, 2.5, 5);
const DEFAULT_CAM = new THREE.Vector3(2.5, 1.8, 3);
const CONTROLS_TARGET = new THREE.Vector3(0, 0.4, 0);
const IDLE_DELAY_MS = 1000;
const autoRotate = true;
const AUTO_ROTATE_SPEED = 0.2;
const MIN_DISTANCE = 3;
const MAX_DISTANCE = 6;
const DEFAULT_ZOOM_ENABLED = true;
const DEFAULT_BG_COLOR = '#EFECE8';
const DEFAULT_BG_VISIBLE = true;
const DEFAULT_SHADOW_ENABLED = true;
const DEFAULT_AMBIENT_INTENSITY = 0.7;
const DEFAULT_SUN_INTENSITY = 1.8;
const DEFAULT_SUN_POSITION = [4, 6, 3];
const DEFAULT_FILL_INTENSITY = 0.4;
const DEFAULT_FILL_POSITION = [-3, 2, -2];

// WebP is bij een 3D-render ~10x kleiner dan PNG bij vergelijkbare kwaliteit én
// houdt transparantie, waardoor de multipart-upload naar /cart/add.js klein
// blijft. Eenmalig meten op een 1x1 canvas; Safari < 14 valt terug op jpg/png.
let _webpSupport = null;
function supportsWebp() {
  if (_webpSupport === null) {
    const probe = document.createElement('canvas');
    probe.width = probe.height = 1;
    _webpSupport = probe.toDataURL('image/webp').startsWith('data:image/webp');
  }
  return _webpSupport;
}

// --- Chroom / spiegelend frame -------------------------------------------
// Alleen deze FRAME_FINISH code krijgt de chroom-behandeling. Alle andere
// finishes (top én frame) blijven exact de bestaande texture-route volgen.
const CHROME_FINISH_CODE = 'SS0200';
const CHROME_PRESET = {
  color: 0xffffff,      // gekleurde basis dempt de reflectie — wit laten
  metalness: 1.0,
  roughness: 0.1,      // hoger = zachter/geborsteld, 0.2 ≈ RVS
  envMapIntensity: 10.5, // omhoog voor feller chroom, omlaag bij uitgeblazen highlights
};
// De environment map wordt alleen als material.envMap gezet op de chroom-meshes,
// NIET als scene.environment. Daardoor verandert er niets aan hout, lak of stof
// en hoeven de bestaande lichtintensiteiten niet opnieuw gebalanceerd te worden.

// Tone mapping werkt renderer-breed en verandert dus ook de niet-chroom
// materialen. Standaard uit gelaten; zet op true als chroom-highlights te vlak
// wit dichtklappen — verwacht dan wel dat de rest iets zachter/donkerder wordt.
const ENABLE_TONE_MAPPING = false;

// --- Losse rand (EDGETYPE) als eigen GLB ---------------------------------
// De rand-GLB heet naar de vórm van de rand, niet naar de gekozen kleur, zodat
// een extra randkleur alleen een texture kost en geen tweede kopie van
// hetzelfde mesh. Edgetype-specifieke namen worden wél eerst geprobeerd, voor
// producten waar de rand per type echt een andere vorm heeft.
const EDGE_FINISH_STATE_KEYS = ['EDGE_FINISH', 'EDGETYPE_FINISH', 'EDGE_COLOUR', 'EDGE_COLOR'];

// Edgetypes waarvan de rand al in het blad-GLB zit. Bij deze codes wordt géén
// losse rand geladen (dat zou een dubbele rand geven), en bij álle andere codes
// dient hetzelfde bestand als "kaal" blad waar de losse rand overheen komt.
const BAKED_EDGETYPES = ['STD'];

// Hoekradius van het blad. De stapcode kan per product verschillen; de eerste
// die in de state voorkomt wint. Ontbreekt hij, dan blijven alle bestandsnamen
// exact zoals ze nu zijn.
const CORNER_RADIUS_STATE_KEYS = ['CR', 'CORNER_RADIUS', 'RADIUS'];

customElements.define(
  'studio-henk-3d-viewer',
  class StudioHenk3DViewer extends HTMLElement {
    connectedCallback() {
      this._files = this._parseFiles(this.dataset.files || '');
      this._texFiles = this._parseFiles(this.dataset.textures || '');
      this._viewerSettings = this._parseViewerSettings(this.dataset.viewerSettings || '');

      const hasGlb = this._files.some(f => f.format === 'glb');
      const hasImages = this._files.some(f => f.format === 'image');

      if (!this._files.length) {
        // Geen files → viewer verbergen, configurator toont eigen preview
        this.hidden = true;
        return;
      }

      if (!hasGlb && hasImages) {
        // Alleen afbeeldingen → geen Three.js nodig, afbeeldingen via _showImage
        this._imgEl = this.querySelector('.viewer__image');
        this._canvas = this.querySelector('canvas');
        this._placeholder = this.querySelector('.viewer__placeholder');
        this._loadingEl = this.querySelector('.viewer__loading');
        if (this._canvas) this._canvas.hidden = true;
        this._onStateUpdate = (e) => this._updateImageOnly(e.detail);
        window.addEventListener('lt-state-update', this._onStateUpdate);
        if (window._ltState) this._updateImageOnly(window._ltState);
        return;
      }

      this._prevShapeKey = null;
      this._prevEdgeKey = null;
      this._prevEdgeTexKey = null;
      this._prevFrameKey = null;
      this._edgeHasGlb = false;
      this._currentEdgeTex = null;
      this._prevTopTexKey = null;
      this._prevFrmTexKey = null;
      this._prevFallbackKey = null;
      this._shapeHasGlb = false;
      this._frameHasGlb = false;
      this._currentTopTex = null;
      this._currentFrameTex = null;
      this._frameIsChrome = false;
      this._autoRotate = false;

      this._setup();
      this._bindEvents();
      this._scheduleAutoRotate();

      if (window._ltState) this._updateScene(window._ltState);
    }

    _updateImageOnly(state) {
      const articleCode = state.articleCode || '';
      const uPrefix = articleCode ? `${articleCode}_` : '';
      const prefix = articleCode ? `${articleCode}-` : '';

      const size = state.SIZE ? state.SIZE.replace(/^rond-/i, '') : null;

      // Haal alle waarden op uit de state, ongeacht key-namen — SIZE wordt gestript gebruikt
      const vals = Object.entries(state)
        .filter(([k]) => k !== 'articleCode')
        .map(([k, v]) => k === 'SIZE' ? size : v)
        .filter(Boolean);

      const bases = [];

      // Segmenten in volgorde: articleCode, S-codes, TOP_FINISH, fabric, edgetype, mounting, shape, thickness, size
      const SYSTEM_KEYS = new Set(['articleCode', 'EDGETYPE', 'THICKNESS', 'SIZE', 'SHAPE', 'MOUNTING', 'FRAME', 'FRAME_FINISH']);
      const dynamicVals = Object.entries(state).filter(([k]) => !SYSTEM_KEYS.has(k) && state[k]);

      // S-codes (lak/materiaal) uit dynamische keys, O-code uit TOP_FINISH
      const sCodes = [...new Set(dynamicVals.map(([, v]) => v).filter(v => /^S\d+/.test(v)))];
      const topFinish = state.TOP_FINISH || state.COLOUR || state.COLOR ||
        dynamicVals.map(([, v]) => v).find(v => /^O\d+/.test(v)) || null;
      const fabric = dynamicVals.map(([, v]) => v).find(v => /^F\d+/.test(v)) || null;
      const edgetype = state.EDGETYPE || null;
      const mounting = state.MOUNTING || null;
      const shape = state.SHAPE || null;
      const thickness = state.THICKNESS || null;      


      // Kandidaten in volgorde: S-codes_TOP_FINISH_fabric_edgetype_mounting_shape_thickness_size
      const orderedParts = [...sCodes, topFinish, fabric, edgetype, mounting, shape, thickness, size].filter(Boolean);
      for (let len = orderedParts.length; len >= 2; len--) {
        bases.push(`${uPrefix}${orderedParts.slice(0, len).join('_')}`);
      }

      // Alle permutaties van 3 waarden (bijv. LT00000404_S1002_O9017_C)
      for (let i = 0; i < vals.length; i++) {
        for (let j = 0; j < vals.length; j++) {
          for (let k = 0; k < vals.length; k++) {
            if (i !== j && i !== k && j !== k) {
              bases.push(`${uPrefix}${vals[i]}_${vals[j]}_${vals[k]}`);
            }
          }
        }
      }

      // Alle permutaties van 2 waarden (bijv. LT00000456_S9005_cyber-beige05)
      for (let i = 0; i < vals.length; i++) {
        for (let j = 0; j < vals.length; j++) {
          if (i !== j) bases.push(`${uPrefix}${vals[i]}_${vals[j]}`);
        }
      }

      // Enkele waarden
      for (const v of vals) {
        bases.push(`${uPrefix}${v}`, `${prefix}${v}`);
      }

      console.group('[3D] image-only state');
      console.table([
        { key: 'articleCode', value: articleCode || '—' },
        { key: 'S-codes', value: sCodes.join(', ') || '—' },
        { key: 'TOP_FINISH (O-code)', value: topFinish || '—' },
        { key: 'fabric (F-code)', value: fabric || '—' },
        { key: 'edgetype (EDGETYPE)', value: edgetype || '—' },
        { key: 'mounting (MOUNTING)', value: mounting || '—' },
        { key: 'shape (SHAPE)', value: shape || '—' },
        { key: 'thickness (THICKNESS)', value: thickness || '—' },
        { key: 'size (SIZE)', value: size || '—' },
        { key: 'primary candidate', value: `${uPrefix}${orderedParts.join('_')}` },
      ]);
      console.groupEnd();

      const candidates = bases.flatMap(b => [`${b}.avif`, `${b}.jpg`, `${b}.png`, b]);
      const matched = this._findFirstFile(candidates);
      
      if (matched?.url) {
        this._showImage(matched.url);
      } else {
        console.warn('[3D] image-only geen match — beschikbaar:', this._files.map(f => f.filename));
      }
    }

    disconnectedCallback() {
      this._ro?.disconnect();
      this._mq?.removeEventListener('change', this._onMqChange);
      if (this._onStateUpdate) window.removeEventListener('lt-state-update', this._onStateUpdate);
      clearTimeout(this._idleTimer);
      cancelAnimationFrame(this._raf);
      this._envRT?.dispose();
      this._renderer?.dispose();
    }

    _parseHexColor(hex) {
      const h = (hex || '').replace('#', '');
      if (h.length === 8) {
        const alpha = parseInt(h.slice(6, 8), 16) / 255;
        return { color: `#${h.slice(0, 6)}`, alpha };
      }
      return { color: `#${h}`, alpha: 1 };
    }

    _parseViewerSettings(str) {
      const defaults = {
        default_angle: null,
        zoom_functie: DEFAULT_ZOOM_ENABLED,
        min_distance: MIN_DISTANCE,
        max_distance: MAX_DISTANCE,
        min_polar_angle: 0.1,
        max_polar_angle: 0.475,
        achtergrond_tonen: DEFAULT_BG_VISIBLE,
        achtergrond_kleur: DEFAULT_BG_COLOR,
        schaduw: DEFAULT_SHADOW_ENABLED,
        ambient_intensiteit: DEFAULT_AMBIENT_INTENSITY,
        zon_intensiteit: DEFAULT_SUN_INTENSITY,
        zon_positie: DEFAULT_SUN_POSITION.join(','),
        fill_intensiteit: DEFAULT_FILL_INTENSITY,
        fill_positie: DEFAULT_FILL_POSITION.join(','),
      };
      if (!str) {
        console.warn('[3D] geen data-viewer-settings op het element — alle defaults actief');
        return defaults;
      }
      try {
        const parsed = { ...defaults, ...JSON.parse(str) };
        console.log('[3D] viewer settings:', parsed);
        return parsed;
      } catch (err) {
        console.warn('[3D] data-viewer-settings is geen geldige JSON — alle instellingen vallen terug op defaults:', err.message, str);
      }
      return defaults;
    }

    _parseFiles(str) {
      if (!str) return [];
      try { return this._normalizeFiles(JSON.parse(str)); } catch {}
      // Shopify Liquid Ruby hash formaat: {"key"=>"value"}{"key"=>"value"}
      try {
        const json = '[' + str.replace(/=>/g, ':').replace(/\}\s*\{/g, '},{') + ']';
        return this._normalizeFiles(JSON.parse(json));
      } catch {}
      return [];
    }

    _normalizeFiles(raw) {
      return raw.map(f => {
        const filename = f.filename || f.name || '';
        const ext = filename.split('.').pop().toLowerCase();
        const format = f.format || (ext === 'glb' || ext === 'gltf' ? 'glb' : 'image');
        return { ...f, filename, format };
      });
    }

    // default_angle: "azimut" of "azimut,elevatie" in graden, toegepast op de offset
    // t.o.v. CONTROLS_TARGET. Azimut 0° = recht van voren (+Z), positief draait naar
    // rechts; elevatie is de hoek boven de horizon. Afstand blijft ongewijzigd.
    _applyDefaultAngle(offset, raw) {
      if (raw == null || raw === '') return offset;
      const parts = String(raw).split(',').map(Number);
      if (!parts.length || parts.some(n => isNaN(n))) return offset;
      const sph = new THREE.Spherical().setFromVector3(offset);
      sph.theta = THREE.MathUtils.degToRad(parts[0]);
      if (parts.length > 1) sph.phi = THREE.MathUtils.degToRad(90 - parts[1]);
      sph.makeSafe();
      return offset.setFromSpherical(sph);
    }

    _setup() {
      this._canvas = this.querySelector('canvas');
      this._imgEl = this.querySelector('.viewer__image');
      this._placeholder = this.querySelector('.viewer__placeholder');
      this._loadingEl = this.querySelector('.viewer__loading');

      const s = this._viewerSettings;

      this._renderer = new THREE.WebGLRenderer({ canvas: this._canvas, antialias: true, preserveDrawingBuffer: true, alpha: true });
      this._renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      this._renderer.outputColorSpace = THREE.SRGBColorSpace;
      if (ENABLE_TONE_MAPPING) {
        this._renderer.toneMapping = THREE.ACESFilmicToneMapping;
        this._renderer.toneMappingExposure = 1.0;
      }
      const shadowEnabled = s.schaduw != null ? Boolean(s.schaduw) : DEFAULT_SHADOW_ENABLED;
      this._renderer.shadowMap.enabled = shadowEnabled;
      this._renderer.shadowMap.type = THREE.PCFSoftShadowMap;

      this._scene = new THREE.Scene();
      this._scene.background = null;
      this._renderer.setClearColor(0x000000, 0);

      if ((s.achtergrond_tonen ?? DEFAULT_BG_VISIBLE) !== false) {
        const { color: bgColor, alpha: bgAlpha } = this._parseHexColor(s.achtergrond_kleur || DEFAULT_BG_COLOR);
        this._renderer.setClearColor(bgColor, bgAlpha);
        this._scene.background = bgAlpha >= 1 ? new THREE.Color(bgColor) : null;
        this._captureBg = bgColor;
      } else {
        this._scene.background = null;
        this._renderer.setClearColor(0x000000, 0);
        this._captureBg = null;
      }

      const parseVec3Pos = (str) => {
        if (!str) return null;
        const [x, y, z] = String(str).split(',').map(Number);
        return (!isNaN(x) && !isNaN(y) && !isNaN(z)) ? new THREE.Vector3(x, y, z) : null;
      };

      const minDist = s.min_distance != null ? Number(s.min_distance) : MIN_DISTANCE;
      const rawCamPos = parseVec3Pos(s.startpositie) ?? DEFAULT_CAM.clone();
      const camOffset = this._applyDefaultAngle(rawCamPos.clone().sub(CONTROLS_TARGET), s.default_angle);
      this._camPos = CONTROLS_TARGET.clone().add(camOffset.normalize().multiplyScalar(minDist));

      this._camera = new THREE.PerspectiveCamera(40, 1, 0.01, 100);
      this._camera.position.copy(this._camPos);
      this._camera.lookAt(CONTROLS_TARGET);

      this._controls = new OrbitControls(this._camera, this._canvas);
      this._controls.enableZoom = s.zoom_functie != null ? Boolean(s.zoom_functie) : DEFAULT_ZOOM_ENABLED;
      this._controls.enableDamping = true;
      this._controls.dampingFactor = 0.06;
      this._controls.target.copy(CONTROLS_TARGET);
      this._controls.autoRotateSpeed = AUTO_ROTATE_SPEED;
      this._controls.minDistance = s.min_distance != null ? Number(s.min_distance) : MIN_DISTANCE;
      this._controls.maxDistance = s.max_distance != null ? Number(s.max_distance) : MAX_DISTANCE;
      this._controls.minPolarAngle = Math.PI * (s.min_polar_angle != null ? Number(s.min_polar_angle) : 0.1);
      this._controls.maxPolarAngle = Math.PI * (s.max_polar_angle != null ? Number(s.max_polar_angle) : 0.475);
      this._controls.enabled = true;
      this._controls.update();

      const parseVec3 = (str, def) => {
        if (!str) return def;
        const parts = String(str).split(',').map(Number);
        return parts.length === 3 && parts.every(n => !isNaN(n)) ? parts : def;
      };

      this._scene.add(new THREE.AmbientLight(0xffffff, s.ambient_intensiteit != null ? Number(s.ambient_intensiteit) : DEFAULT_AMBIENT_INTENSITY));

      const sunPos = parseVec3(s.zon_positie, DEFAULT_SUN_POSITION);
      const sun = new THREE.DirectionalLight(0xffffff, s.zon_intensiteit != null ? Number(s.zon_intensiteit) : DEFAULT_SUN_INTENSITY);
      sun.position.set(...sunPos);
      sun.castShadow = shadowEnabled;
      sun.shadow.mapSize.set(1024, 1024);
      this._scene.add(sun);

      const fillPos = parseVec3(s.fill_positie, DEFAULT_FILL_POSITION);
      const fill = new THREE.DirectionalLight(0xffffff, s.fill_intensiteit != null ? Number(s.fill_intensiteit) : DEFAULT_FILL_INTENSITY);
      fill.position.set(...fillPos);
      this._scene.add(fill);

      // Aparte groepen zodat shape en frame parallel geladen worden
      this._topGroup = new THREE.Group();
      this._edgeGroup = new THREE.Group();
      this._frameGroup = new THREE.Group();
      this._scene.add(this._topGroup, this._edgeGroup, this._frameGroup);

      this._gltfLoader = new GLTFLoader();
      this._texLoader = new THREE.TextureLoader();

      this._ro = new ResizeObserver(() => this._resize());
      this._ro.observe(this);
      this._resize();

      const toggleId = `viewer-3d-${Math.random().toString(36).slice(2, 7)}`;
      this._toggleEl = document.createElement('label');
      this._toggleEl.className = 'viewer__toggle';
      this._toggleEl.htmlFor = toggleId;
      this._toggleEl.innerHTML = `
        <input type="checkbox" id="${toggleId}" class="viewer__toggle-input">
        <span class="viewer__toggle-track"><span class="viewer__toggle-thumb"></span></span>
        <span class="viewer__toggle-label">${window.translations?.pdp?.rotate_freely ?? ""}</span>
      `;
      this.appendChild(this._toggleEl);
      this._toggleInput = this._toggleEl.querySelector('input');
      this._toggleInput.addEventListener('change', () => this._onToggle());

      this._mq = window.matchMedia('(min-width: 1180px)');
      this._onMqChange = () => this._applyViewportMode();
      this._mq.addEventListener('change', this._onMqChange);
      this._applyViewportMode();

      const animate = () => {
        this._raf = requestAnimationFrame(animate);
        this._tick();
        this._renderer.render(this._scene, this._camera);
      };
      animate();
    }

    _tick() {
      this._controls.autoRotate = this._autoRotate;
      this._controls.update();
    }

    _scheduleAutoRotate() {
      clearTimeout(this._idleTimer);
      this._idleTimer = setTimeout(() => { this._autoRotate = true; }, IDLE_DELAY_MS);
    }

    _applyViewportMode() {
      const isDesktop = this._mq.matches;
      this._canvas.style.pointerEvents = (isDesktop || this._toggleInput.checked) ? 'auto' : 'none';
      this._toggleEl.style.display = isDesktop ? 'none' : '';
    }

    _onToggle() {
      this._applyViewportMode();
      this._autoRotate = false;
      clearTimeout(this._idleTimer);
      this._scheduleAutoRotate();
    }

    _bindEvents() {
      this._onStateUpdate = (e) => this._updateScene(e.detail);
      window.addEventListener('lt-state-update', this._onStateUpdate);

      this._canvas.addEventListener('pointerdown', () => {
        this._autoRotate = false;
        clearTimeout(this._idleTimer);
      });
      this._canvas.addEventListener('pointerup', () => {
        this._scheduleAutoRotate();
      });
    }

    _resize() {
      const w = this.clientWidth;
      const h = this.clientHeight;
      if (!w || !h) return;
      this._renderer.setSize(w, h);
      this._camera.aspect = w / h;
      this._camera.updateProjectionMatrix();
    }

    _findFile(filename) {
      const base = filename.toLowerCase().replace(/\.[^.]+$/, '');
      const entry = this._files.find(f => {
        const fBase = (f.filename || '').toLowerCase().replace(/\.[^.]+$/, '');
        return fBase === base;
      });
      if (!entry) console.warn('[3D] geen bestand gevonden voor:', filename, '— beschikbaar:', this._files.map(f => f.filename));
      return entry || null;
    }

    _findFileSilent(filename) {
      const base = filename.toLowerCase().replace(/\.[^.]+$/, '');
      return this._files.find(f => {
        const fBase = (f.filename || '').toLowerCase().replace(/\.[^.]+$/, '');
        return fBase === base;
      }) || null;
    }

    _findFirstFile(candidates) {
      for (const candidate of candidates) {
        const f = this._findFileSilent(candidate);
        if (f) return f;
      }
      return null;
    }

    _showImage(url) {
      this._imgEl.hidden = false;
      if (this._canvas) this._canvas.hidden = true;
      if (this._placeholder) this._placeholder.hidden = true;

      if (this._currentImgUrl === url) return;
      this._currentImgUrl = url;

      if (this._loadingEl) this._loadingEl.hidden = false;

      const img = new Image();
      img.onload = () => {
        this._imgEl.src = url;
        if (this._loadingEl) this._loadingEl.hidden = true;
      };
      img.onerror = () => {
        if (this._loadingEl) this._loadingEl.hidden = true;
      };
      img.src = url;
    }

    _showCanvas() {
      this._canvas.hidden = false;
      this._imgEl.hidden = true;
    }

    get currentImageUrl() {
      return (this._imgEl && !this._imgEl.hidden && this._currentImgUrl) || null;
    }

    captureAsFile(width = 1200) {
      return new Promise((resolve) => {
        // Image-only modus: geen File. De getoonde afbeelding staat al op de
        // Shopify CDN (data-files komt uit `image_url`), dus die downloaden en
        // daarna byte-voor-byte weer uploaden naar /cart/add.js kost twee
        // netwerkrondes voor exact hetzelfde plaatje. De configurator pakt in
        // plaats daarvan `currentImageUrl` als property-waarde.
        if (this.currentImageUrl) { resolve(null); return; }

        // 3D modus: render vanuit default positie en exporteer als File
        if (!this._renderer || !this._scene || !this._camera) { resolve(null); return; }

        const savedPos = this._camera.position.clone();
        const savedTarget = this._controls?.target.clone();

        this._camera.position.copy(this._camPos ?? DEFAULT_CAM);
        this._camera.lookAt(CONTROLS_TARGET);
        if (this._controls) { this._controls.target.copy(CONTROLS_TARGET); this._controls.update(); }
        this._renderer.render(this._scene, this._camera);

        const height = Math.round(width * (3 / 4));
        const offscreen = document.createElement('canvas');
        offscreen.width = width;
        offscreen.height = height;
        const ctx = offscreen.getContext('2d');
        if (this._captureBg) {
          ctx.fillStyle = this._captureBg;
          ctx.fillRect(0, 0, width, height);
        }
        ctx.drawImage(this._canvas, 0, 0, width, height);

        const webp = supportsWebp();
        const mime = webp ? 'image/webp' : this._captureBg ? 'image/jpeg' : 'image/png';
        const ext = webp ? 'webp' : this._captureBg ? 'jpg' : 'png';
        offscreen.toBlob((blob) => {
          this._camera.position.copy(savedPos);
          if (this._controls && savedTarget) { this._controls.target.copy(savedTarget); this._controls.update(); }
          resolve(blob ? new File([blob], `preview.${ext}`, { type: mime }) : null);
        }, mime, 0.85);
      });
    }

    capture(width = 400) {
      // Image-only modus: geef de huidige afbeeldings-URL terug (vermijdt canvas CORS-problemen)
      if (this._imgEl && !this._imgEl.hidden && this._currentImgUrl) {
        return this._currentImgUrl;
      }

      // 3D modus: render vanuit de default positie
      if (!this._renderer || !this._scene || !this._camera) return null;

      const savedPos = this._camera.position.clone();
      const savedTarget = this._controls?.target.clone();

      this._camera.position.copy(this._camPos ?? DEFAULT_CAM);
      this._camera.lookAt(CONTROLS_TARGET);
      if (this._controls) {
        this._controls.target.copy(CONTROLS_TARGET);
        this._controls.update();
      }
      this._renderer.render(this._scene, this._camera);

      const height = Math.round(width * (3 / 4));
      const offscreen = document.createElement('canvas');
      offscreen.width = width;
      offscreen.height = height;
      const ctx2d = offscreen.getContext('2d');
      if (this._captureBg) {
        ctx2d.fillStyle = this._captureBg;
        ctx2d.fillRect(0, 0, width, height);
      }
      ctx2d.drawImage(this._canvas, 0, 0, width, height);
      const dataUrl = this._captureBg
        ? offscreen.toDataURL('image/jpeg', 0.7)
        : offscreen.toDataURL('image/png');

      // Herstel camera positie
      this._camera.position.copy(savedPos);
      if (this._controls && savedTarget) {
        this._controls.target.copy(savedTarget);
        this._controls.update();
      }

      return dataUrl;
    }

    _clearGroup(group) {
      while (group.children.length) {
        const obj = group.children[0];
        obj.traverse(n => {
          if (!n.isMesh) return;
          n.geometry.dispose();
          const mats = Array.isArray(n.material) ? n.material : [n.material];
          mats.forEach(m => m.dispose());
        });
        group.remove(obj);
      }
    }

    // Environment map wordt pas aangemaakt zodra er daadwerkelijk chroom in beeld
    // komt. Producten zonder SS0200 betalen dus geen enkele kost.
    _getEnvTexture() {
      if (this._envTexture) return this._envTexture;
      if (!this._renderer) return null;
      try {
        const pmrem = new THREE.PMREMGenerator(this._renderer);
        this._envRT = pmrem.fromScene(new RoomEnvironment(), 0.04);
        this._envTexture = this._envRT.texture;
        pmrem.dispose();
        console.log('[3D] environment map aangemaakt voor chroom');
      } catch (err) {
        console.warn('[3D] environment map aanmaken mislukt:', err.message);
        this._envTexture = null;
      }
      return this._envTexture;
    }

    _loadTexture(code, onLoad) {
      if (!this._texFiles?.length) return onLoad(null);
      const base = code.toLowerCase();
      const entry = this._texFiles.find(f => {
        const fBase = (f.filename || '').toLowerCase().replace(/\.[^.]+$/, '');
        return fBase === base;
      });
      if (!entry) {
        console.warn('[3D] geen texture gevonden voor:', code, '— beschikbaar:', this._texFiles.map(f => f.filename));
        return onLoad(null);
      }
      console.log('[3D] loading texture:', entry.url);
      this._texLoader.load(
        entry.url,
        (tex) => { tex.colorSpace = THREE.SRGBColorSpace; onLoad(tex); },
        undefined,
        () => { console.warn('[3D] texture laden mislukt:', entry.filename); onLoad(null); }
      );
    }

    _applyTextureToGroup(group, tex) {
      group.traverse(n => {
        if (!n.isMesh) return;
        n.material = new THREE.MeshStandardMaterial({
          map: tex,
          color: tex ? 0xffffff : 0xcccccc,
          roughness: 0.85,
          metalness: 0,
        });
      });
    }

    // Alleen aangeroepen voor het frame bij FRAME_FINISH === SS0200.
    _applyChromeToGroup(group) {
      const envMap = this._getEnvTexture();
      group.traverse(n => {
        if (!n.isMesh) return;
        const old = Array.isArray(n.material) ? n.material : [n.material];
        old.forEach(m => m?.dispose());
        n.material = new THREE.MeshStandardMaterial({
          color: CHROME_PRESET.color,
          metalness: CHROME_PRESET.metalness,
          roughness: CHROME_PRESET.roughness,
          envMap: envMap || null,
          envMapIntensity: CHROME_PRESET.envMapIntensity,
        });
      });
      console.log('[3D] chroom toegepast op frame');
    }

    _loadIntoGroup(file, group) {
      if (file.format === 'image') {
        this._clearGroup(group);
        this._showImage(file.url);
        return;
      }
      this._showCanvas();
      this._clearGroup(group);
      console.log('[3D] loading GLB:', file.url);
      this._gltfLoader.load(
        file.url,
        (gltf) => {
          this._clearGroup(group);
          gltf.scene.traverse(n => {
            if (!n.isMesh) return;
            n.castShadow = this._renderer.shadowMap.enabled;
            n.receiveShadow = this._renderer.shadowMap.enabled;
          });
          group.add(gltf.scene);
          // Pas bestaande finish toe als die al bekend is
          if (group === this._frameGroup && this._frameIsChrome) {
            this._applyChromeToGroup(group);
          } else {
              const tex = group === this._topGroup ? this._currentTopTex
            : group === this._edgeGroup ? this._currentEdgeTex
            : this._currentFrameTex;
            if (tex) this._applyTextureToGroup(group, tex);
          }
          if (this._placeholder) this._placeholder.hidden = true;
        },
        undefined,
        (err) => {
          console.warn('[3D] GLB laden mislukt:', file.filename, err.message);
          const fallback = file.previewUrl || file.preview_url;
          if (fallback) this._showImage(fallback);
        }
      );
    }

    _cornerRadius(state) {
      for (const k of CORNER_RADIUS_STATE_KEYS) if (state[k]) return state[k];
      return null;
    }


    // Blad waarin de rand van déze EDGETYPE al gebakken zit. Bestaat dat, dan
    // is het altijd beter dan een losse rand: het is de enige variant die zowel
    // de randvorm als de hoekradius klopt. Bestaat het niet (E0008 en andere
    // split-edge types), dan valt de lookup door naar de losse rand.
    _bakedTopFor(prefix, state) {
      const shape = state.SHAPE || '';
      if (!shape || !state.EDGETYPE) return null;
      const t = state.THICKNESS ? `-${state.THICKNESS}` : '';
      const radius = this._cornerRadius(state);
      const cr = radius ? `-${radius}` : '';
      const thicks = t ? [t, ''] : [''];
      const cands = [];

      for (const r of (cr ? [cr, ''] : [''])) {
        for (const th of thicks) {
          cands.push(`${prefix}${shape}${th}-${state.EDGETYPE}${r}.glb`);
          if (r) cands.push(`${prefix}${shape}${th}${r}-${state.EDGETYPE}.glb`);
        }
      }
      return this._findFirstFile(cands);
    }



    // Blad-GLB. Radius weegt visueel zwaarder dan een kale vs. gebakken rand,
    // dus eerst álle CR-specifieke namen en pas daarna dezelfde reeks zonder CR
    // — producten waar de radius de geometrie niet verandert vallen daar vanzelf
    // op terug.
    _topFileFor(prefix, state) {
      const shape = state.SHAPE || '';
      if (!shape) return null;
      const t = state.THICKNESS ? `-${state.THICKNESS}` : '';
      const radius = this._cornerRadius(state);
      const cr = radius ? `-${radius}` : '';

      // Suffixen die de rand in het blad bakken. Bij een losse rand-GLB de
      // gekozen EDGETYPE overslaan, anders zou de rand dubbel staan.
      const edgeSuffixes = [...new Set([
        ...(!this._edgeHasGlb && state.EDGETYPE ? [`-${state.EDGETYPE}`] : []),
        ...BAKED_EDGETYPES.map(b => `-${b}`),
      ])];

      const thicks = t ? [t, ''] : [''];
      const radii = cr ? [cr, ''] : [''];
      const cands = [];

      for (const r of radii) {
        // Kaal blad eerst — dat wint van een variant met gebakken rand
        for (const th of thicks) cands.push(`${prefix}${shape}${th}${r}.glb`);
        for (const edge of edgeSuffixes) {
          for (const th of thicks) {
            // Conventie: <shape>-<dikte>-<edge>-<radius>
            cands.push(`${prefix}${shape}${th}${edge}${r}.glb`);
            // Omgekeerde volgorde als vangnet voor oudere uploads
            if (r) cands.push(`${prefix}${shape}${th}${r}${edge}.glb`);
          }
        }
      }

      const file = this._findFirstFile(cands);
      if (!file) {
        console.warn('[3D] geen blad-GLB gevonden — kandidaten:', cands,
          '— beschikbaar:', this._files.filter(f => f.format === 'glb').map(f => f.filename));
      }
      return file;
    }


    _edgeFileFor(prefix, state) {
      if (!state.EDGETYPE) return null;
      if (BAKED_EDGETYPES.some(e => e.toLowerCase() === String(state.EDGETYPE).toLowerCase())) return null;
      const shape = state.SHAPE || '';
      const t = state.THICKNESS ? `-${state.THICKNESS}` : '';
      const radius = this._cornerRadius(state);
      const cr = radius ? `-${radius}` : '';
      const thicks = t ? [t, ''] : [''];
      const cands = [];

      if (shape) {
        for (const th of thicks) {
          cands.push(`${prefix}${shape}${th}-${state.EDGETYPE}${cr}-edge.glb`);
          cands.push(`${prefix}${shape}${th}${cr}-${state.EDGETYPE}-edge.glb`);
          cands.push(`${prefix}${shape}${th}${cr}-edge.glb`);
        }
      }
      // Radius-loze randen als laatste redmiddel. Een radius-specifieke rand
      // wint altijd; bestaat die niet, dan is de generieke rand beter dan géén
      // losse rand — zonder rand vervalt namelijk ook de aparte randkleur.
      if (shape) {
        for (const code of [state.EDGETYPE]) {
          for (const th of thicks) cands.push(`${prefix}${shape}${th}-${code}-edge.glb`);
        }
        for (const th of thicks) cands.push(`${prefix}${shape}${th}-edge.glb`);
      }
      cands.push(`${prefix}${state.EDGETYPE}-edge.glb`);
      cands.push(`${prefix}edge.glb`);

      const file = this._findFirstFile(cands);
      if (file && cr && !file.filename.toLowerCase().includes(String(radius).toLowerCase())) {
        console.log('[3D] rand zonder radius gebruikt bij', radius, '—', file.filename,
          '. Upload een radius-variant als de hoeken niet kloppen.');
      }
      return file;
    }

    // Kandidaat-codes voor de rand-texture, in volgorde van specifiek naar
    // algemeen. Zolang er geen aparte EDGE_FINISH-stap bestaat wordt de
    // EDGETYPE-code zelf als kleurcode geprobeerd, met TOP_FINISH als vangnet.
    _edgeFinishCodes(state) {
      const codes = [];
      for (const k of EDGE_FINISH_STATE_KEYS) if (state[k]) codes.push(state[k]);
      if (state.EDGETYPE) codes.push(state.EDGETYPE);
      if (state.TOP_FINISH) codes.push(state.TOP_FINISH);
      return [...new Set(codes)];
    }

    _loadTextureChain(codes, onLoad) {
      const next = (i) => {
        if (i >= codes.length) {
          console.warn('[3D] geen rand-texture gevonden voor:', codes,
            '— beschikbaar:', this._texFiles?.map(f => f.filename) ?? []);
          return onLoad(null);
        }
        const target = String(codes[i]).toLowerCase();
        const entry = this._texFiles?.find(f =>
          (f.filename || '').toLowerCase().replace(/\.[^.]+$/, '') === target);
        if (!entry) return next(i + 1);
        this._texLoader.load(
          entry.url,
          (tex) => { tex.colorSpace = THREE.SRGBColorSpace; onLoad(tex); },
          undefined,
          () => next(i + 1)
        );
      };
      next(0);
    }


    _updateScene(state) {
      const prefix = state.articleCode ? `${state.articleCode}-` : '';
      const uPrefix = state.articleCode ? `${state.articleCode}_` : '';
      
      // --- Blad + rand ---
      // Eerst kijken of er een blad met gebakken rand voor deze EDGETYPE is.
      // Zo ja: dat blad, geen losse rand. Zo nee: losse rand + kaal blad.
      const bakedTop = this._bakedTopFor(prefix, state);
      const edgeFile = bakedTop ? null : this._edgeFileFor(prefix, state);
      this._edgeHasGlb = !!edgeFile;

      const edgeKey = edgeFile?.filename || null;
      if (edgeKey !== this._prevEdgeKey) {
        this._prevEdgeKey = edgeKey;
        if (edgeFile) this._loadIntoGroup(edgeFile, this._edgeGroup);
        else this._clearGroup(this._edgeGroup);
      }

      const shapeFile = bakedTop || this._topFileFor(prefix, state);
      const shapeKey = shapeFile?.filename || null;
      if (shapeKey !== this._prevShapeKey) {
        this._prevShapeKey = shapeKey;
        this._shapeHasGlb = !!shapeFile;
        if (shapeFile) this._loadIntoGroup(shapeFile, this._topGroup);
        else this._clearGroup(this._topGroup);
      }

      // --- Frame ---
      const frameKey = [state.SHAPE, state.FRAME].filter(Boolean).join('-') || null;
      if (frameKey !== this._prevFrameKey) {
        this._prevFrameKey = frameKey;
        if (state.FRAME) {
          const frameCandidates = [];
          if (state.SHAPE) frameCandidates.push(`${prefix}${state.SHAPE}-${state.FRAME}.glb`);
          frameCandidates.push(`${prefix}${state.FRAME}.glb`);
          console.log('[3D] frame zoeken — state.SHAPE:', state.SHAPE, 'state.FRAME:', state.FRAME, 'kandidaten:', frameCandidates);
          console.log('[3D] beschikbare GLB bestanden:', this._files.filter(f => f.format === 'glb').map(f => f.filename));
          const file = this._findFirstFile(frameCandidates);
          console.log('[3D] frame gevonden:', file?.filename ?? 'geen');
          if (file) {
            this._frameHasGlb = true;
            this._loadIntoGroup(file, this._frameGroup);
          } else {
            this._frameHasGlb = false;
            this._clearGroup(this._frameGroup);
          }
        } else {
          this._frameHasGlb = false;
          this._clearGroup(this._frameGroup);
        }
      }

      // --- Fallback image when no GLBs are found ---
      const fallbackKey = [state.SHAPE, state.FRAME, state.TOP_FINISH, state.FRAME_FINISH].filter(Boolean).join('|');
      if (fallbackKey !== this._prevFallbackKey) {
        this._prevFallbackKey = fallbackKey;
        if (!this._shapeHasGlb && !this._frameHasGlb && !this._edgeHasGlb) {
          this._tryFallbackImage(state, prefix, uPrefix);
        }
      }

      // --- Textures ---
      const topTexKey = state.TOP_FINISH || null;
      if (topTexKey !== this._prevTopTexKey) {
        this._prevTopTexKey = topTexKey;
        if (state.TOP_FINISH) {
          this._loadTexture(state.TOP_FINISH, (tex) => {
            this._currentTopTex = tex;
            if (this._topGroup.children.length) this._applyTextureToGroup(this._topGroup, tex);
          });
        } else {
          this._currentTopTex = null;
        }
      }

      const edgeCodes = this._edgeFinishCodes(state);
      const edgeTexKey = edgeCodes.join('|');
      if (edgeTexKey !== this._prevEdgeTexKey) {
        this._prevEdgeTexKey = edgeTexKey;
        this._loadTextureChain(edgeCodes, (tex) => {
          this._currentEdgeTex = tex;
          if (this._edgeGroup.children.length) this._applyTextureToGroup(this._edgeGroup, tex);
        });
      }

      const frmTexKey = state.FRAME_FINISH || null;
      if (frmTexKey !== this._prevFrmTexKey) {
        this._prevFrmTexKey = frmTexKey;
        const isChrome = String(state.FRAME_FINISH || '').toUpperCase() === CHROME_FINISH_CODE;
        if (isChrome) {
          // Chroom: geen texture, spiegelend materiaal met environment map
          this._frameIsChrome = true;
          this._currentFrameTex = null;
          if (this._frameGroup.children.length) this._applyChromeToGroup(this._frameGroup);
        } else if (state.FRAME_FINISH) {
          // Ongewijzigd: bestaande texture-route voor alle andere finishes
          this._frameIsChrome = false;
          this._loadTexture(state.FRAME_FINISH, (tex) => {
            this._currentFrameTex = tex;
            if (this._frameGroup.children.length) this._applyTextureToGroup(this._frameGroup, tex);
          });
        } else {
          this._frameIsChrome = false;
          this._currentFrameTex = null;
        }
      }
    }

    _tryFallbackImage(state, prefix, uPrefix) {
      const shape = state.SHAPE || '';
      const frame = state.FRAME || '';
      const topFin = state.TOP_FINISH || '';
      const frmFin = state.FRAME_FINISH || '';
      const t = state.THICKNESS ? `-${state.THICKNESS}` : '';
      const edge = state.EDGETYPE ? `-${state.EDGETYPE}` : '';

      const bases = [];

      // Underscore format (e.g. LT00000456_S7031_cyber-beige05)
      if (shape && topFin) bases.push(`${uPrefix}${shape}_${topFin}`);
      if (shape && frmFin) bases.push(`${uPrefix}${shape}_${frmFin}`);
      if (frame && frmFin) bases.push(`${uPrefix}${frame}_${frmFin}`);
      if (frame && topFin) bases.push(`${uPrefix}${frame}_${topFin}`);

      // Dash format with finish
      if (shape && topFin) bases.push(`${prefix}${shape}${t}${edge}-${topFin}`);
      if (shape && topFin) bases.push(`${prefix}${shape}-${topFin}`);
      if (frame && frmFin) bases.push(`${prefix}${frame}-${frmFin}`);

      // Shape/frame only (no finish)
      if (shape) bases.push(`${uPrefix}${shape}`, `${prefix}${shape}${t}${edge}`, `${prefix}${shape}`);
      if (frame) bases.push(`${uPrefix}${frame}`, `${prefix}${frame}`);

      const candidates = bases.flatMap(b => [`${b}.avif`, `${b}.jpg`, `${b}.png`, b]);
      console.log('[3D] fallback image zoeken — kandidaten:', candidates);
      const found = this._findFirstFile(candidates);
      if (found?.url) {
        console.log('[3D] fallback image gevonden:', found.filename, '→', found.url);
        this._showImage(found.url);
      } else {
        console.warn('[3D] geen fallback image gevonden — beschikbaar:', this._files.map(f => f.filename));
      }
    }
  }
); 